import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import Icon from '../../components/AppIcon';
import { fetchAppEventLogs, fetchAdminActivityLogs } from '../../lib/adminApi';

const appFilters = [
  { value: 'all', label: 'All apps' },
  { value: 'customer', label: 'Customer' },
  { value: 'caterer', label: 'Caterer' },
  { value: 'admin', label: 'Admin' },
];

const levelFilters = [
  { value: 'all', label: 'All levels' },
  { value: 'error', label: 'Error' },
  { value: 'warn', label: 'Warn' },
  { value: 'info', label: 'Info' },
  { value: 'debug', label: 'Debug' },
  { value: 'fatal', label: 'Fatal' },
];

const levelStyles = {
  error: 'bg-rose-100 text-rose-700',
  warn: 'bg-amber-100 text-amber-700',
  info: 'bg-sky-100 text-sky-700',
  debug: 'bg-muted text-muted-foreground',
  fatal: 'bg-purple-100 text-purple-700',
};

export default function ObservabilityPage() {
  const [appFilter, setAppFilter] = useState('all');
  const [levelFilter, setLevelFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [appLogs, setAppLogs] = useState([]);
  const [adminLogs, setAdminLogs] = useState([]);
  const [loadingApp, setLoadingApp] = useState(true);
  const [loadingAdmin, setLoadingAdmin] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    setLoadingApp(true);
    setError(null);
    fetchAppEventLogs({
      app: appFilter === 'all' ? undefined : appFilter,
      level: levelFilter === 'all' ? undefined : levelFilter,
      search,
      limit: 200,
    })
      .then((data) => { if (isMounted) setAppLogs(data); })
      .catch((err) => { if (isMounted) setError(err?.message || 'Failed to load app logs'); })
      .finally(() => { if (isMounted) setLoadingApp(false); });
    return () => { isMounted = false; };
  }, [appFilter, levelFilter, search]);

  useEffect(() => {
    let isMounted = true;
    setLoadingAdmin(true);
    fetchAdminActivityLogs({ limit: 200 })
      .then((data) => { if (isMounted) setAdminLogs(data); })
      .catch((err) => { if (isMounted) setError(err?.message || 'Failed to load admin activity'); })
      .finally(() => { if (isMounted) setLoadingAdmin(false); });
    return () => { isMounted = false; };
  }, []);

  return (
    <div className="space-y-8">
      <header className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Observability</h2>
          <p className="text-sm text-muted-foreground">
            Unified error stream from customer, caterer, and admin web apps.
          </p>
        </div>
      </header>

      {error && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      <section className="space-y-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h3 className="text-lg font-semibold text-foreground">App Error Logs</h3>
            <p className="text-xs text-muted-foreground">Filtered to 200 most recent entries.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2">
              <Icon name="Search" size={16} className="text-muted-foreground" />
              <input
                type="search"
                placeholder="Search message..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              />
            </div>
            {appFilters.map((f) => (
              <button
                key={f.value}
                onClick={() => setAppFilter(f.value)}
                className={`rounded-full px-3 py-1 text-xs font-semibold ${
                  appFilter === f.value ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'
                }`}
              >
                {f.label}
              </button>
            ))}
            {levelFilters.map((f) => (
              <button
                key={f.value}
                onClick={() => setLevelFilter(f.value)}
                className={`rounded-full px-3 py-1 text-xs font-semibold ${
                  levelFilter === f.value ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border text-sm">
              <thead className="bg-muted/30 text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Time</th>
                  <th className="px-4 py-3 text-left font-semibold">App</th>
                  <th className="px-4 py-3 text-left font-semibold">Level</th>
                  <th className="px-4 py-3 text-left font-semibold">Message</th>
                  <th className="px-4 py-3 text-left font-semibold">User</th>
                  <th className="px-4 py-3 text-left font-semibold">Route</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/80">
                {loadingApp && (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center text-muted-foreground">Loading…</td>
                  </tr>
                )}
                {!loadingApp && appLogs.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center text-muted-foreground">No logs found.</td>
                  </tr>
                )}
                {!loadingApp && appLogs.map((log) => (
                  <tr key={log.id} className="hover:bg-muted/30">
                    <td className="px-4 py-3 text-muted-foreground">
                      {log.created_at ? format(new Date(log.created_at), 'dd MMM yyyy HH:mm') : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-semibold text-foreground">
                        {log.app_name}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                        levelStyles[log.level] ?? 'bg-muted text-foreground'
                      }`}>
                        {log.level}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="max-w-md truncate text-foreground" title={log.message}>
                        {log.message}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {log.user_id ? String(log.user_id).slice(0, 8) + '…' : '—'}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {log.route ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="space-y-4">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Admin Activity</h3>
          <p className="text-xs text-muted-foreground">Last 200 admin actions.</p>
        </div>
        <div className="rounded-xl border border-border bg-card shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border text-sm">
              <thead className="bg-muted/30 text-muted-foreground">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Time</th>
                  <th className="px-4 py-3 text-left font-semibold">Admin</th>
                  <th className="px-4 py-3 text-left font-semibold">Action</th>
                  <th className="px-4 py-3 text-left font-semibold">Entity</th>
                  <th className="px-4 py-3 text-left font-semibold">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/80">
                {loadingAdmin && (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center text-muted-foreground">Loading…</td>
                  </tr>
                )}
                {!loadingAdmin && adminLogs.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-6 py-8 text-center text-muted-foreground">No admin activity found.</td>
                  </tr>
                )}
                {!loadingAdmin && adminLogs.map((log) => (
                  <tr key={log.id} className="hover:bg-muted/30">
                    <td className="px-4 py-3 text-muted-foreground">
                      {log.created_at ? format(new Date(log.created_at), 'dd MMM yyyy HH:mm') : '—'}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {log.admin?.email ?? String(log.admin_user_id).slice(0, 8) + '…'}
                    </td>
                    <td className="px-4 py-3">
                      <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-semibold text-foreground">
                        {log.activity_type}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {log.entity_type ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      <div className="max-w-md truncate" title={log.description ?? ''}>
                        {log.description ?? '—'}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
}
